/**
 * Hall of Fame graphic — a downloadable PNG summary of the pool leaderboard.
 *
 * Design goals:
 *   - Winner is visually dominant (large gold card at the top).
 *   - Each subsequent rank shrinks progressively (2nd is medium, 3rd smaller,
 *     the rest scale down towards a compact row).
 *   - Prize the winner takes home is highlighted in accent green (SEK or the
 *     pool's chosen currency).
 *   - Bonus points, group points, knockout points and total are all shown.
 *   - Portrait 1200x{auto} canvas, dark navy background matching the app.
 *
 * Rendered entirely with the Canvas 2D API — no extra dependencies, one-click
 * PNG download via `canvas.toBlob`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Download, Share2, X, Trophy } from "lucide-react";

const CANVAS_WIDTH = 1200;

// Layout sizing per rank. Height + font weights scale down as rank grows so
// the champion visually dominates. Rank 1 = huge, 2/3 = medium podium,
// 4+ progressively compact rows.
function cardSpecFor(rank, totalPlayers) {
    if (rank === 1) return { height: 380, primary: 96, name: 76, meta: 30, showTrophy: true, accent: "#FFD700" };
    if (rank === 2) return { height: 220, primary: 64, name: 52, meta: 24, showTrophy: false, accent: "#C0C0C0" };
    if (rank === 3) return { height: 180, primary: 52, name: 44, meta: 22, showTrophy: false, accent: "#CD7F32" };
    // Ranks 4..N — scale from ~140 down to ~78. Never smaller than 78 so text
    // stays legible even in a 20-player pool.
    const remaining = Math.max(1, totalPlayers - 3);
    const step = Math.min(1, (rank - 4) / Math.max(1, remaining - 1));
    const height = Math.round(140 - step * 60);
    return { height, primary: 36, name: 30, meta: 18, showTrophy: false, accent: "#8892B0" };
}

function drawRoundedRect(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function formatCurrency(amount, currency) {
    if (!amount || amount <= 0) return null;
    const rounded = Math.round(amount);
    const cur = (currency || "").trim() || "SEK";
    // Add thin-space separators for readability.
    const withSeparators = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "\u2009");
    return `${withSeparators} ${cur}`;
}

function truncate(ctx, text, maxWidth) {
    if (!text) return "";
    if (ctx.measureText(text).width <= maxWidth) return text;
    let lo = 0, hi = text.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        const candidate = text.slice(0, mid) + "…";
        if (ctx.measureText(candidate).width <= maxWidth) lo = mid;
        else hi = mid - 1;
    }
    return text.slice(0, lo) + "…";
}

/**
 * Render the entire Hall of Fame graphic onto the given canvas element.
 * Returns the final canvas height in pixels.
 */
function renderHallOfFame(canvas, rows, pool) {
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    // First pass — compute total height needed.
    const HEADER = 260;
    const FOOTER = 100;
    const CARD_GAP = 18;
    const specs = rows.map((r) => cardSpecFor(r.rank, rows.length));
    const contentHeight = specs.reduce((acc, s) => acc + s.height + CARD_GAP, 0);
    const totalHeight = HEADER + contentHeight + FOOTER;

    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = totalHeight * dpr;
    canvas.style.width = "100%";
    canvas.style.height = "auto";

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.textBaseline = "middle";

    // ─── background ─────────────────────────────────────────
    const bg = ctx.createLinearGradient(0, 0, 0, totalHeight);
    bg.addColorStop(0, "#0A1024");
    bg.addColorStop(0.5, "#0D1533");
    bg.addColorStop(1, "#050915");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_WIDTH, totalHeight);

    // subtle grain — random dots
    ctx.save();
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = "#FFFFFF";
    for (let i = 0; i < 400; i += 1) {
        ctx.fillRect(Math.random() * CANVAS_WIDTH, Math.random() * totalHeight, 1, 1);
    }
    ctx.restore();

    // ─── header ─────────────────────────────────────────────
    ctx.fillStyle = "#16A34A";
    ctx.font = "700 26px 'Inter', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("FIFA WORLD CUP 2026 · HALL OF FAME", CANVAS_WIDTH / 2, 60);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "800 64px 'Inter', system-ui, sans-serif";
    const poolName = truncate(ctx, (pool?.name || "Pool").toUpperCase(), CANVAS_WIDTH - 120);
    ctx.fillText(poolName, CANVAS_WIDTH / 2, 130);

    ctx.fillStyle = "#8892B0";
    ctx.font = "500 22px 'Inter', system-ui, sans-serif";
    const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    ctx.fillText(`Final standings · ${dateStr} · ${rows.length} players`, CANVAS_WIDTH / 2, 175);

    // Prize pot chip — big and green
    const prize = formatCurrency(pool?.total_prize, pool?.bet_currency);
    if (prize) {
        ctx.font = "700 22px 'Inter', system-ui, sans-serif";
        const label = "PRIZE POT";
        const value = prize;
        const chipW = Math.max(320, ctx.measureText(value).width + 220);
        const chipX = (CANVAS_WIDTH - chipW) / 2;
        const chipY = 205;
        drawRoundedRect(ctx, chipX, chipY, chipW, 40, 20);
        ctx.fillStyle = "#16A34A";
        ctx.fill();
        ctx.textAlign = "center";
        ctx.fillStyle = "#0A1024";
        ctx.font = "800 16px 'Inter', system-ui, sans-serif";
        ctx.fillText(label, chipX + 60, chipY + 20);
        ctx.font = "800 22px 'Inter', system-ui, sans-serif";
        ctx.fillText(value, chipX + chipW - (chipW - 120) / 2, chipY + 20);
    }

    // ─── ranked cards ───────────────────────────────────────
    let y = HEADER;
    rows.forEach((row, idx) => {
        const spec = specs[idx];
        const cardX = 40;
        const cardW = CANVAS_WIDTH - 80;
        const cardH = spec.height;

        // card background — winner gets a warm gold glow
        drawRoundedRect(ctx, cardX, y, cardW, cardH, 24);
        if (row.rank === 1) {
            const grad = ctx.createLinearGradient(cardX, y, cardX + cardW, y);
            grad.addColorStop(0, "rgba(255, 215, 0, 0.18)");
            grad.addColorStop(1, "rgba(22, 163, 74, 0.22)");
            ctx.fillStyle = grad;
        } else {
            ctx.fillStyle = "rgba(19, 27, 54, 0.85)";
        }
        ctx.fill();
        ctx.strokeStyle = row.rank === 1 ? "#FFD700" : row.rank === 2 ? "#C0C0C0" : row.rank === 3 ? "#CD7F32" : "rgba(255,255,255,0.08)";
        ctx.lineWidth = row.rank <= 3 ? 3 : 1;
        ctx.stroke();

        // Rank badge on the left
        const badgeCX = cardX + 90;
        const badgeCY = y + cardH / 2;
        const badgeR = Math.min(cardH * 0.35, 70);
        ctx.beginPath();
        ctx.arc(badgeCX, badgeCY, badgeR, 0, Math.PI * 2);
        ctx.fillStyle = spec.accent;
        ctx.fill();
        ctx.fillStyle = "#0A1024";
        ctx.font = `900 ${Math.round(badgeR * 1.2)}px 'Inter', system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(String(row.rank), badgeCX, badgeCY + 2);

        // Player name + meta (left-aligned after the badge)
        const nameX = cardX + 180;
        const meta = [];
        meta.push(`Group ${row.group_points_total ?? 0}p`);
        meta.push(`Knockout ${row.knockout_points_total ?? 0}p`);
        meta.push(`Bonus ${row.bonus_points_total ?? 0}p`);

        ctx.textAlign = "left";
        ctx.fillStyle = "#FFFFFF";
        ctx.font = `800 ${spec.name}px 'Inter', system-ui, sans-serif`;
        const nameMaxW = cardW - 180 - 220; // leave room for total column
        const displayName = truncate(ctx, (row.user_name || "—").toUpperCase(), nameMaxW);
        ctx.fillText(displayName, nameX, badgeCY - spec.name * 0.35);

        ctx.fillStyle = "#8892B0";
        ctx.font = `600 ${spec.meta}px 'Inter', system-ui, sans-serif`;
        ctx.fillText(meta.join("  ·  "), nameX, badgeCY + spec.meta * 1.2);

        // Total points on the right — bright accent
        ctx.textAlign = "right";
        ctx.fillStyle = row.rank === 1 ? "#FFD700" : "#16A34A";
        ctx.font = `900 ${spec.primary}px 'Inter', system-ui, sans-serif`;
        ctx.fillText(String(row.total_points), cardX + cardW - 30, badgeCY - 4);
        ctx.fillStyle = "#8892B0";
        ctx.font = `700 ${Math.round(spec.meta * 0.8)}px 'Inter', system-ui, sans-serif`;
        ctx.fillText("POINTS", cardX + cardW - 30, badgeCY + spec.primary * 0.55);

        // Winner extras — trophy icon + prize call-out
        if (spec.showTrophy) {
            // trophy glyph (simple emoji-free vector)
            ctx.save();
            ctx.strokeStyle = "#FFD700";
            ctx.fillStyle = "#FFD700";
            ctx.lineWidth = 4;
            const tX = cardX + cardW - 30;
            const tY = y + 46;
            // small "1st PLACE" ribbon above points
            ctx.textAlign = "right";
            ctx.font = "800 20px 'Inter', system-ui, sans-serif";
            ctx.fillText("★ 1ST PLACE ★", tX, tY);
            ctx.restore();

            if (prize) {
                // Prize call-out below name
                ctx.textAlign = "left";
                const prizeText = `TAKES HOME ${prize}`;
                ctx.font = "800 30px 'Inter', system-ui, sans-serif";
                const w = ctx.measureText(prizeText).width;
                const pyChip = badgeCY + spec.meta * 2.4;
                drawRoundedRect(ctx, nameX, pyChip - 22, w + 40, 44, 22);
                ctx.fillStyle = "#16A34A";
                ctx.fill();
                ctx.fillStyle = "#0A1024";
                ctx.fillText(prizeText, nameX + 20, pyChip + 1);
            }
        }

        y += cardH + CARD_GAP;
    });

    // ─── footer ─────────────────────────────────────────────
    ctx.textAlign = "center";
    ctx.fillStyle = "#4A5578";
    ctx.font = "500 20px 'Inter', system-ui, sans-serif";
    ctx.fillText("wc2026.wollin.eu · Generated automatically from the live leaderboard", CANVAS_WIDTH / 2, totalHeight - 55);
    ctx.fillStyle = "#16A34A";
    ctx.font = "800 22px 'Inter', system-ui, sans-serif";
    ctx.fillText("VM · TIPS 2026", CANVAS_WIDTH / 2, totalHeight - 25);

    return totalHeight;
}

export default function HallOfFameModal({ open, onClose, rows, pool }) {
    const canvasRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [sharing, setSharing] = useState(false);

    // Sort rows by rank ascending, defensively — server should already return
    // sorted but we don't want to trust it for a visual snapshot.
    const sortedRows = useMemo(() => {
        const clone = [...(rows || [])];
        clone.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
        return clone;
    }, [rows]);

    useEffect(() => {
        if (!open || !canvasRef.current || sortedRows.length === 0) return;
        renderHallOfFame(canvasRef.current, sortedRows, pool);
    }, [open, sortedRows, pool]);

    const safeName = (pool?.name || "pool").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const fileName = `hall-of-fame-${safeName}.png`;

    const canvasToFile = useCallback(() => new Promise((resolve) => {
        if (!canvasRef.current) { resolve(null); return; }
        canvasRef.current.toBlob((blob) => {
            if (!blob) { resolve(null); return; }
            resolve(new File([blob], fileName, { type: "image/png" }));
        }, "image/png");
    }), [fileName]);

    const download = useCallback(async () => {
        setBusy(true);
        try {
            const file = await canvasToFile();
            if (!file) { setBusy(false); return; }
            const url = URL.createObjectURL(file);
            const link = document.createElement("a");
            link.download = fileName;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setBusy(false);
        }
    }, [canvasToFile, fileName]);

    // Web Share API — mobile devices (iOS Safari / Chrome Android) can share
    // the PNG directly to WhatsApp / iMessage / Telegram / etc. Desktop
    // support is spottier; we feature-detect `canShare({ files })` and fall
    // back to a plain `share({ title, text, url })` when file sharing isn't
    // available, then to a toast that instructs the user to Download instead.
    const canShareFiles = typeof navigator !== "undefined"
        && typeof navigator.canShare === "function"
        && typeof navigator.share === "function";
    const canShareAtAll = typeof navigator !== "undefined" && typeof navigator.share === "function";

    const share = useCallback(async () => {
        if (!canShareAtAll) {
            toast.info("Sharing isn't supported on this device — use Download PNG and attach it manually.");
            return;
        }
        setSharing(true);
        try {
            const title = `${(pool?.name || "Pool")} — Final standings`;
            const text = `Final results from our FIFA World Cup 2026 pool "${pool?.name || "our pool"}". Check out the winner!`;
            const file = await canvasToFile();
            if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title, text });
            } else {
                // Fallback: no file support — share title + text + current page URL.
                await navigator.share({ title, text, url: window.location.href });
            }
        } catch (e) {
            // User cancelled → AbortError. Anything else, tell them.
            if (e && e.name !== "AbortError") {
                toast.error(`Sharing failed: ${e.message || e.name || "unknown error"}`);
            }
        } finally {
            setSharing(false);
        }
    }, [canShareAtAll, canvasToFile, pool]);

    if (!open) return null;

    return createPortal((
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="hall-of-fame-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
                <div className="flex items-center justify-between p-4 border-b border-[var(--border-muted)]">
                    <div className="flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-[var(--accent-hex)]" />
                        <h3 className="font-heading text-xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]">League Summary</h3>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                        {canShareAtAll && (
                            <button onClick={share} disabled={sharing}
                                data-testid="hall-of-fame-share"
                                className="inline-flex items-center gap-2 border border-[var(--accent-hex)] text-[var(--accent-hex)] font-extrabold uppercase tracking-widest text-xs px-4 py-2 rounded-md hover:bg-[var(--accent-hex)]/10 disabled:opacity-50">
                                <Share2 className="h-4 w-4" />
                                {sharing ? "…" : "Share"}
                            </button>
                        )}
                        <button onClick={download} disabled={busy}
                            data-testid="hall-of-fame-download"
                            className="inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-widest text-xs px-4 py-2 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50">
                            <Download className="h-4 w-4" />
                            {busy ? "…" : "Download PNG"}
                        </button>
                        <button onClick={onClose} data-testid="hall-of-fame-close"
                            className="p-2 rounded-md border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <div className="overflow-auto p-4 bg-black/40">
                    <canvas ref={canvasRef} data-testid="hall-of-fame-canvas" className="w-full h-auto rounded-md shadow-xl" />
                </div>
                <div className="p-3 border-t border-[var(--border-muted)] text-xs text-[var(--text-secondary)] text-center">
                    Share this graphic wherever you brag — the podium sizes reflect the final points and the winner&apos;s prize is highlighted. Tap <strong>Share</strong> to send it via WhatsApp / iMessage / Telegram (mobile), or <strong>Download PNG</strong> to save it.
                </div>
            </div>
        </div>
    ), document.body);
}
